import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import React, { memo, useState, useRef, useEffect, useContext } from "react";
import { UseFormReturnType } from "@mantine/form";
import { IFormProps } from ".";
import Select, { ISelect } from "src/components/Select";
import NumberInput from "src/components/NumberInput/NumberInput";
import ImageUpload from "src/components/ImageUpload";
import Switch from "src/components/Switch/Switch";
import {
  Box,
  Group,
  Grid,
  Avatar,
  Flex,
  useMantineTheme,
  Text,
} from "@mantine/core";
import ActionCard from "src/components/ActionCard";
import DatePicker from "src/components/DatePicker";
import TimeInput from "src/components/TimeInput";
import { HeightCard } from "src/utils/constants";
import {
  GetLocationServicesQuery,
  GetMeetingSeriesDetailsQuery,
  GetPrivateSessionsDetailQuery,
  InputMaybe,
  SeriesInterval,
  useGetAttendeeNamesQuery,
  useGetEmployeesNameQuery,
  useGetLocationServicesQuery,
  useGetPrivateSessionsDetailQuery,
} from "src/generated/graphql";
import {
  getDateDiff,
  TimeDiffType,
  daysOfWeekShort,
} from "src/utils/dateHelper";
import { AppContext } from "src/api/AppContext";
import MultiSelect from "src/components/MultiSelect";

type IForm = {
  form: UseFormReturnType<IFormProps>;
  isEdit: boolean;
  meetingDetail: GetMeetingSeriesDetailsQuery["meetingSeriesOne"] | undefined;
  disabled: boolean;
};

type IRooms = ISelect & { totalcapacity: number };

function MeetingSeriesForm({ form, isEdit, meetingDetail, disabled }: IForm) {
  const theme = useMantineTheme();
  const { currentLocation, currentOrganization } = useContext(AppContext);
  const bg = theme.colors.secondary[3];
  const color = theme.colors.secondaryTextColor[3];
  const currentServiceDetail = useRef<
    GetLocationServicesQuery["locationServiceMany"][0] | undefined
  >();
  const timeInputRef = useRef<HTMLInputElement | null>(null);

  const [roomsData, setRoomsData] = useState<IRooms[]>([]);
  const [instructorData, setInstructorData] = useState<ISelect[]>([]);
  const [maxAttendee, setMaxAttendee] = useState(Infinity);
  const [minAttendee, setMinAttendee] = useState(0);
  const [minWaitlist, setMinWaitlist] = useState(0);

  const isPrivateSession = !!form.getInputProps("isPrivateSession").value;
  const [sessionData, setsessionData] =
    useState<
      NonNullable<GetPrivateSessionsDetailQuery["privateSessionMany"]>[0]
    >();
  const locationId = isEdit
    ? meetingDetail?.location.id!
    : currentLocation?.id!;
  const { data: servicesData } = useGetLocationServicesQuery({
    variables: {
      locationId,
    },
    onCompleted: () => {
      let _detail = servicesData?.locationServiceMany.find(
        (e) => e.id === meetingDetail?.service?.id
      );
      setRoomsData(
        _detail?.rooms?.map((val) => ({
          value: val.id,
          label: val.name,
          totalcapacity: val.totalCapacity,
        })) || []
      );
    },
    skip: !locationId,
    fetchPolicy: "network-only",
  });

  const { data: employeeData } = useGetEmployeesNameQuery({
    variables: { locationId },
    onCompleted: () => {
      setInstructorData(
        employeeData?.employeeMany?.map((val) => ({
          value: val.id,
          label: `${val.user.firstName} ${val.user.lastName}`,
        })) || []
      );
    },
    skip: !locationId,
    fetchPolicy: "network-only",
  });

  const { data: sessions } = useGetPrivateSessionsDetailQuery({
    variables: { locationId: currentLocation?.id || "" },
    skip: !isPrivateSession || !currentLocation?.id,
    fetchPolicy: "network-only",
  });

  const { data: attendeesData } = useGetAttendeeNamesQuery({
    variables: { orgId: currentOrganization?.id || "" },
    skip: !isPrivateSession || !currentOrganization?.id,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (meetingDetail) {
      let _detail = servicesData?.locationServiceMany.find(
        (e) => e.id === meetingDetail.service?.id
      );
      currentServiceDetail.current = _detail;
      if (meetingDetail.session?.id && sessions?.privateSessionMany) {
        handleSessionChange(meetingDetail.session.id);
      }

      const {
        room,
        instructor,
        service,
        scheduleFrom,
        scheduleTo,
        startAt,
        duration,
        isPrivateSession,
        session,
        privateSession,
        attendees,
        ...rest
      } = meetingDetail;
      form.setValues({
        ...rest,
        service: service?.id,
        instructor: instructor?.id,
        room: room?.id,
        scheduleFrom: new Date(scheduleFrom),
        scheduleTo: new Date(scheduleTo),
        startAt: new Date(startAt),
        duration: duration || 0,
        location: currentLocation?.id!,
        isPrivateSession,
        session: session?.id,
        privateSession: privateSession?.id,
        isEmployeeRequired: true,
        isRoomRequired: true,
        attendees: attendees?.map((a) => a.id) || [],
        days:
          meetingDetail.seriesInterval === SeriesInterval.Daily
            ? getDateDiff(
                new Date(scheduleFrom),
                new Date(scheduleTo),
                TimeDiffType.days
              )
            : undefined,
      });
    }
  }, [meetingDetail, sessions]);

  const handleServiceChange = (id: string, isEdit: boolean = false) => {
    let _detail = servicesData?.locationServiceMany.find((e) => e.id === id);
    currentServiceDetail.current = _detail;
    setRoomsData(
      _detail?.rooms?.map((val) => ({
        value: val.id,
        label: val.name,
        totalcapacity: val.totalCapacity,
      })) || []
    );

    setInstructorData(
      employeeData?.employeeMany?.map((val) => ({
        value: val.id,
        label: `${val.user.firstName} ${val.user.lastName}`,
      })) || []
    );
    setMaxAttendee(Infinity);
    if (!isEdit) {
      form.setValues({
        ..._detail?.organizationService,
        service: id,
        room: "",
        instructor: "",
      });
    }
  };

  const handleIntervalChange = (interval: InputMaybe<SeriesInterval>) => {
    form.setFieldValue("seriesInterval", interval);
    form.setFieldValue("doms", []);
    form.setFieldValue("dows", []);
  };

  const handleRoomChange = (id: string) => {
    form.setFieldValue("room", id);
    let _max = roomsData.find((e) => e.value === id)?.totalcapacity || 0;
    form.setFieldValue("maxOccupancy", _max);
    setMaxAttendee(Number(_max));
  };

  const toggleWeeks = (day: number) => {
    if (form.values.dows?.includes(day)) {
      form.setFieldValue(
        "dows",
        form.values.dows.filter((d) => d !== day)
      );
    } else {
      form.values.dows &&
        form.setFieldValue("dows", [...form.values.dows, day]);
    }
  };

  const toggleMonths = (day: number) => {
    if (form.values.doms?.includes(day)) {
      form.setFieldValue(
        "doms",
        form.values.doms.filter((d) => d !== day)
      );
    } else {
      form.values.doms &&
        form.setFieldValue("doms", [...form.values.doms, day]);
    }
  };

  // cases for session change
  const handleToggle = (bool: boolean) => {
    form.setValues({
      ...form.values,
      isPrivateSession: bool,
      dows: [],
      doms: [],
      scheduleFrom: undefined,
      scheduleTo: undefined,
      days: undefined,
      service: "",
      session: "",
      privateSession: "",
    });
    setInstructorData([]);
    setRoomsData([]);
  };

  const handleSessionChange = (id: string) => {
    const { duration, room, privateSession } = form.values || {};
    form.setFieldValue("session", id);
    let _detail = sessions?.privateSessionMany?.find((e) => e.id === id);
    !_detail?.isEmployeeRequired &&
      form.setFieldValue("isEmployeeRequired", false);
    !_detail?.isRoomRequired && form.setFieldValue("isRoomRequired", false);
    setsessionData(_detail);
    // set Rooms Array if isRoomRequired
    const validRooms =
      _detail?.rooms
        .flatMap((roomGroup) => roomGroup.rooms)
        .map((e) => ({
          value: e.id,
          label: e.name,
          totalcapacity: e.totalCapacity,
        })) || [];
    _detail?.isRoomRequired && setRoomsData(validRooms);

    // check for valid room on change sessionId
    if (room && !validRooms.find((r) => r.value === room)) {
      form.setFieldValue("room", "");
    }

    if (
      privateSession &&
      !_detail?.durations.find((d) => d.id === privateSession)
    ) {
      form.setFieldValue("privateSession", "");
    }

    validDurations(duration, _detail!);
  };

  const validDurations = (
    _duration: number,
    sessionData: NonNullable<
      GetPrivateSessionsDetailQuery["privateSessionMany"]
    >[0]
  ) => {
    const { instructor } = form.values || {};
    const filteredOverrides =
      sessionData
        ?.overrides!.filter(
          (override) =>
            _duration >= (override?.minDuration || sessionData.minDuration!) &&
            _duration <= (override?.maxDuration || sessionData.maxDuration!)
        )
        .map((o) => ({
          value: o.employee.id,
          label: `${o.employee.user.firstName} ${o.employee.user.lastName}`,
        })) || [];
    setInstructorData(filteredOverrides);
    if (instructor && !filteredOverrides.find((o) => o.value === instructor)) {
      form.setFieldValue("instructor", "");
    }
  };

  const handleDurationSession = (id: string) => {
    form.setFieldValue("privateSession", id);
    let _duration = sessionData?.durations.find((d) => d.id === id)?.duration!;
    form.setFieldValue("duration", _duration);
    validDurations(_duration, sessionData!);
  };

  const { imgUrl, seriesInterval, session, service, isWaitlistEnabled, room } =
    form.values;

  return (
    <>
      <ActionCard title="General" height={HeightCard.md}>
        <Switch
          sx={{ width: "50%", margin: "auto" }}
          labelPosition="left"
          label="Private Session"
          checked={isPrivateSession}
          disabled={isEdit || disabled}
          {...form.getInputProps("isPrivateSession")}
          onChange={(e) => handleToggle(e.target.checked)}
        />

        {isPrivateSession ? (
          <Select
            disabled={isEdit || disabled}
            label="Private Session"
            mb={"sm"}
            data={
              sessions?.privateSessionMany?.map((s) => ({
                value: s.id,
                label: s.name,
              })) || []
            }
            {...form.getInputProps("session")}
            onChange={handleSessionChange}
          />
        ) : (
          <Select
            withAsterisk
            data={
              servicesData?.locationServiceMany?.map((val) => ({
                value: val.id,
                label: val.organizationService.name,
              })) || []
            }
            label="Service"
            mb={"sm"}
            disabled={isEdit || disabled}
            {...form.getInputProps("service")}
            onChange={handleServiceChange}
          />
        )}

        <Group position="apart">
          <ImageUpload
            disabled={disabled}
            onReadSuccess={(b64) => form.setFieldValue("imgUrl", b64)}
            src={form.values.imgUrl || ""}
            alt="Service Image"
            sx={{
              maxHeight: 170,
              maxWidth: 200,
              margin: "auto",
              minHeight: 170,
            }}
          />

          <Box style={{ alignItems: "center" }}>
            <Switch
              disabled={disabled}
              labelPosition="left"
              label="Self-Enroll"
              checked={!!form.getInputProps("isSelfEnroll")?.value}
              {...form.getInputProps("isSelfEnroll")}
            />
            {!isPrivateSession && (
              <Switch
                disabled={disabled}
                mt={"sm"}
                labelPosition="left"
                label="Enable Waitlist"
                checked={!!form.getInputProps("isWaitlistEnabled")?.value}
                {...form.getInputProps("isWaitlistEnabled")}
              />
            )}
          </Box>
        </Group>

        <TextInput
          withAsterisk
          label="Name"
          disabled={disabled}
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          disabled={disabled}
          {...form.getInputProps("description")}
        />

        {isWaitlistEnabled && !isPrivateSession && (
          <NumberInput
            disabled={disabled}
            withAsterisk
            min={minWaitlist}
            label="Waitlist Max"
            {...form.getInputProps("waitlistMax")}
          />
        )}
      </ActionCard>

      <ActionCard title="Scheduling" mt={"sm"} height={HeightCard.md}>
        {meetingDetail &&
        getDateDiff(new Date(), meetingDetail.startAt, TimeDiffType.hours) <
          1 ? null : (
          <Grid justify="space-between">
            <Grid.Col span={12}>
              <TimeInput
                disabled={disabled}
                ref={timeInputRef}
                withAsterisk
                format="12"
                label="Start Time"
                {...form.getInputProps("startAt")}
                // onChange={handleStartTime}
              />
            </Grid.Col>
          </Grid>
        )}

        {service && (
          <React.Fragment>
            <NumberInput
              disabled={disabled}
              withAsterisk
              label="Duration"
              {...form.getInputProps("duration")}
            />

            <Select
              disabled={disabled}
              withAsterisk
              data={instructorData}
              label="Instructor"
              {...form.getInputProps("instructor")}
            />

            <Select
              disabled={disabled}
              withAsterisk
              data={roomsData}
              label="Room"
              {...form.getInputProps("room")}
              onChange={handleRoomChange}
            />
          </React.Fragment>
        )}

        {session && (
          <>
            <Select
              disabled={disabled}
              withAsterisk
              label="Duration"
              mb={"sm"}
              data={
                sessionData?.durations.map((e) => ({
                  value: e.id,
                  label: "" + e.duration,
                })) || []
              }
              {...form.getInputProps("privateSession")}
              onChange={handleDurationSession}
            />

            {sessionData?.isEmployeeRequired && (
              <Select
                disabled={disabled}
                withAsterisk
                data={instructorData}
                label="Instructor"
                {...form.getInputProps("instructor")}
              />
            )}
            {sessionData?.isRoomRequired && (
              <Select
                withAsterisk
                disabled={disabled}
                data={roomsData}
                label="Room"
                {...form.getInputProps("room")}
                onChange={handleRoomChange}
              />
            )}

            <MultiSelect
              disabled={disabled}
              data={
                attendeesData?.attendeeMany?.map((val) => ({
                  value: val.id,
                  label: `${val.client.user.firstName} ${val.client.user.lastName}`,
                })) || []
              }
              label="Attendees"
              {...form.getInputProps("attendees")}
            />
          </>
        )}

        {service && room && (
          <NumberInput
            disabled={disabled}
            max={maxAttendee}
            min={minAttendee}
            withAsterisk
            label="Occupancy"
            {...form.getInputProps("maxOccupancy")}
          />
        )}
      </ActionCard>

      <ActionCard title="Event Series" mt={"sm"} height={HeightCard.md}>
        <Select
          disabled={disabled}
          withAsterisk
          data={
            Object.keys(SeriesInterval)?.map((val) => ({
              value: val,
              label: val,
            })) || []
          }
          label="Interval"
          {...form.getInputProps("seriesInterval")}
          onChange={(e) => handleIntervalChange(e as SeriesInterval)}
        />

        <Grid justify="space-between">
          <Grid.Col span={seriesInterval === SeriesInterval.Daily ? 12 : 6}>
            <DatePicker
              disabled={disabled}
              withAsterisk
              minDate={new Date()}
              dropdownPosition="top-start"
              label="Schedule From"
              maw={400}
              mx="auto"
              {...form.getInputProps("scheduleFrom")}
            />
          </Grid.Col>
          {seriesInterval !== SeriesInterval.Daily && (
            <Grid.Col span={6}>
              <DatePicker
                disabled={disabled}
                withAsterisk
                dropdownPosition="top-start"
                label="Schedule To"
                maw={400}
                mx="auto"
                {...form.getInputProps("scheduleTo")}
              />
            </Grid.Col>
          )}
        </Grid>

        {!seriesInterval && (
          <NumberInput
            disabled={disabled}
            withAsterisk
            label="Duration"
            {...form.getInputProps("duration")}
          />
        )}

        {seriesInterval === SeriesInterval.Daily && (
          <NumberInput
            disabled={disabled}
            withAsterisk
            label="Days"
            maxLength={5}
            {...form.getInputProps("days")}
          />
        )}

        {seriesInterval === SeriesInterval.Weekly && (
          <>
            <Flex justify={"space-between"} mt={"md"}>
              {daysOfWeekShort.map((e, index) => (
                <Avatar
                  key={e}
                  radius="xl"
                  sx={{ fontWeight: 400 }}
                  styles={{
                    placeholder: {
                      backgroundColor: form.values.dows?.includes(index)
                        ? theme.colors.primary[6]
                        : bg,
                      color: form.values.dows?.includes(index)
                        ? theme.colors.primaryTextColor[6]
                        : color,
                      fontWeight: 600,
                    },
                  }}
                  onClick={() => toggleWeeks(index)}
                >
                  {e}
                </Avatar>
              ))}
            </Flex>
            <Text color="#fa5252" size={12}>
              {form.errors.dows}
            </Text>
          </>
        )}

        {seriesInterval === SeriesInterval.Monthly && (
          <>
            <Flex mt={"md"} gap={"xs"} wrap={"wrap"}>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((e) => (
                <Avatar
                  key={e}
                  radius="xl"
                  size={37}
                  sx={{ fontWeight: 400 }}
                  styles={{
                    placeholder: {
                      backgroundColor: form.values.doms?.includes(e)
                        ? theme.colors.primary[6]
                        : bg,
                      color: form.values.doms?.includes(e)
                        ? theme.colors.primaryTextColor[6]
                        : color,
                      fontWeight: 600,
                    },
                  }}
                  onClick={() => toggleMonths(e)}
                >
                  {e}
                </Avatar>
              ))}
            </Flex>
            <Text color="#fa5252" size={12}>
              {form.errors.doms}
            </Text>
          </>
        )}
      </ActionCard>
    </>
  );
}

export default memo(MeetingSeriesForm);
